import { IngresoConCodigo } from '../activacion/IngresoConCodigo.js';
import { AvisoDeDemostracion } from '../components/domain/AvisoDeDemostracion.js';
import { FirmaDeFundacion } from '../components/domain/FirmaDeFundacion.js';
import { Hero } from '../components/domain/Hero.js';
import { Logo } from '../components/domain/Logo.js';
import { Boton } from '../components/ui/Boton.js';
import { PACIENTE } from '../copy/index.js';

type ValidacionProps = {
  estado: 'validando' | 'error' | 'sin-token';
  onReintentar: () => void;
};

/**
 * P-01 · validación del enlace de acceso.
 *
 * Pantalla centrada, sin navegación: es la primera del sistema y no hay a dónde
 * ir todavía. El mensaje habla de identidad y no de tokens — quien está del otro
 * lado acaba de tocar un link en WhatsApp.
 *
 * El fallo no acusa a nadie. Un enlace vencido es lo más probable y no es culpa
 * del paciente; la salida es pedirle otro al equipo, no reintentar en el vacío.
 *
 * E3.8.2, contra la referencia P-01: la portada donde **el logo es protagonista**,
 * sobre navy con la textura de las cápsulas. Si el enlace no sirve, el aviso va
 * en una tarjeta blanca sobre la misma portada: sigue siendo la puerta del
 * programa, no una pantalla de error.
 *
 * **Sin enlace, se puede entrar con un código de activación** (INGRESO-2), en
 * un bloque aparte debajo: en el iPhone la app instalada no tiene la cookie de
 * Safari, y el enlace se abre en Safari, no en la app. Va siempre, en cualquier
 * dispositivo, sin detectar si la app está instalada.
 */
export function ValidacionMagicLink({ estado, onReintentar }: ValidacionProps) {
  const sinToken = estado === 'sin-token';

  return (
    // La puerta se ve sin sesión: acá va el aviso de demostración (DEMO-1), como
    // parte del layout. Una columna del alto de la ventana, el aviso arriba y la
    // portada con flex-1: juntos no suman más que la ventana.
    //
    // **En escritorio entra entera, sin scroll** (DEMO 06/10): la puerta mide su
    // propio ancho (`@container`) y, cuando tiene lugar, pone las dos tarjetas
    // lado a lado y compacta los huecos. Adentro de una columna angosta —el
    // teléfono, o el reingreso a mitad del onboarding— sigue apilada.
    <div className="@container flex min-h-dvh flex-col">
      <AvisoDeDemostracion />
      <Hero className="flex-1 gap-8 @3xl:gap-5 @3xl:py-6" elemento="section" variante="portada">
        <Logo className="h-auto w-52 @3xl:w-44" fondo="oscuro" />
        <p className="m-0 max-w-[16ch] font-serif text-display-sm @3xl:max-w-none">
          {PACIENTE.acceso.lema}
        </p>
        <div className="flex w-full max-w-md flex-col items-center gap-8 @3xl:max-w-4xl @3xl:flex-row @3xl:items-start @3xl:gap-6 @3xl:*:flex-1">
          {estado === 'validando' ? (
            <p
              aria-live="polite"
              className="m-0 flex items-center gap-3 text-lg text-on-brand-subtle"
              role="status"
            >
              <span
                aria-hidden="true"
                className="size-6 shrink-0 animate-spin rounded-full border-3 border-veil-5 border-t-teal"
              />
              {PACIENTE.acceso.validando}
            </p>
          ) : (
            <div
              className="flex w-full flex-col gap-4 rounded-xl bg-card p-6 text-left text-heading"
              role="alert"
            >
              <h1 className="m-0 font-serif text-2xl font-regular">
                {sinToken ? PACIENTE.acceso.sinTokenTitulo : PACIENTE.acceso.vencidoTitulo}
              </h1>
              <p className="m-0 text-lg text-body">
                {sinToken ? PACIENTE.acceso.sinTokenDetalle : PACIENTE.acceso.vencidoDetalle}
              </p>
              {sinToken ? null : (
                <Boton onClick={onReintentar} variante="secundario">
                  {PACIENTE.acceso.reintentar}
                </Boton>
              )}
            </div>
          )}

          {sinToken ? <IngresoConCodigo /> : null}
        </div>

        <FirmaDeFundacion />
      </Hero>
    </div>
  );
}
